import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, RefreshCw, Check, X, Shield, VideoOff, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../UI/Button';
import { Modal } from '../UI/Modal';
import { LegalModal } from '../UI/LegalModal';
import { facePhotoApi } from '../../api/facePhoto';
import { useAuthStore } from '../../store/authStore';
import styles from './SelfiePrompt.module.css';

interface SelfiePromptProps {
  isOpen: boolean;
  onClose: () => void;
  onCaptured: () => void;
  required?: boolean;
}

type Phase = 'intro' | 'camera' | 'preview' | 'angles';
type AngleStep = 'left' | 'right';

/**
 * Camera-only selfie capture modal using WebRTC.
 * Shows privacy explanation → opens live camera → capture → validate → upload.
 */
export function SelfiePrompt({ isOpen, onClose, onCaptured, required = false }: SelfiePromptProps) {
  const [phase, setPhase] = useState<Phase>('intro');
  const [legalModal, setLegalModal] = useState<'privacy' | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [videoReady, setVideoReady] = useState(false);
  // Optional multi-angle enrollment (front selfie done → offer left/right).
  const [angleStep, setAngleStep] = useState<AngleStep>('left');
  const [angleUploading, setAngleUploading] = useState(false);
  const [anglesDone, setAnglesDone] = useState<Set<AngleStep>>(new Set());

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fetchMe = useAuthStore((s) => s.fetchMe);

  // Stop the camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Start the camera
  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 640 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err: unknown) {
      const e = err as { name?: string };
      if (e.name === 'NotAllowedError') {
        setCameraError('Camera access was denied. Please allow camera access in your browser settings and try again.');
      } else if (e.name === 'NotFoundError') {
        setCameraError('No camera found on this device.');
      } else {
        setCameraError('Could not access your camera. Please check your browser settings.');
      }
    }
  }, []);

  // Open camera when entering a phase that needs it (front capture or angles).
  useEffect(() => {
    const needsCamera = phase === 'camera' || phase === 'angles';
    if (needsCamera && isOpen) {
      startCamera();
    }
    return () => {
      if (!needsCamera) stopCamera();
    };
  }, [phase, isOpen, startCamera, stopCamera]);

  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      // Small delay to let animation finish before resetting state
      const timer = setTimeout(() => {
        setPhase('intro');
        setCapturedBlob(null);
        setPreviewUrl(null);
        setError(null);
        setCameraError(null);
        setVideoReady(false);
        setAngleStep('left');
        setAngleUploading(false);
        setAnglesDone(new Set());
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, stopCamera]);

  // Grab a square, mirrored JPEG frame from the live video. Shared by the
  // front-selfie capture and the optional left/right angle captures.
  const captureSquareFrame = useCallback((): Promise<{ blob: Blob; dataUrl: string } | null> => {
    return new Promise((resolve) => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return resolve(null);

      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) return resolve(null); // metadata not ready yet

      // Center-crop to square (matches the circular camera view the user sees)
      const cropSize = Math.min(vw, vh);
      const sx = (vw - cropSize) / 2;
      const sy = (vh - cropSize) / 2;

      // Cap output at 640×640 — mobile cameras can return 1080p+ which OOMs
      // the browser's image decoder when rendered as an <img> on mobile
      const outSize = Math.min(cropSize, 640);
      canvas.width = outSize;
      canvas.height = outSize;

      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(null);

      // Mirror to match what the user saw in the circular preview
      ctx.translate(outSize, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, sx, sy, cropSize, cropSize, 0, 0, outSize, outSize);

      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      canvas.toBlob(
        (blob) => resolve(blob ? { blob, dataUrl } : null),
        'image/jpeg',
        0.9,
      );
    });
  }, []);

  // Capture the front selfie → preview.
  const handleCapture = useCallback(async () => {
    const frame = await captureSquareFrame();
    if (!frame) return;
    stopCamera();
    setCapturedBlob(frame.blob);
    setPreviewUrl(frame.dataUrl);
    setPhase('preview');
  }, [captureSquareFrame, stopCamera]);

  // Retake
  const handleRetake = useCallback(() => {
    setCapturedBlob(null);
    setPreviewUrl(null);
    setError(null);
    setVideoReady(false);
    setPhase('camera');
  }, []);

  // Upload
  const handleUpload = useCallback(async () => {
    if (!capturedBlob) return;
    setUploading(true);
    setError(null);

    try {
      await facePhotoApi.upload(capturedBlob);
      await fetchMe();
      toast.success('Selfie uploaded successfully!');
      // Offer the optional multi-angle step (better matches on off-angle
      // photos). Fully skippable — the front selfie alone already works.
      setAngleStep('left');
      setAnglesDone(new Set());
      setError(null);
      setVideoReady(false);
      setPhase('angles');
    } catch (err: unknown) {
      if (facePhotoApi.isFaceValidationError(err)) {
        const { code } = err.response.data;
        const messages: Record<string, string> = {
          no_face: "We couldn't detect a face. Please try again with better lighting.",
          multiple_faces: 'Multiple faces detected. Please make sure only your face is in the photo.',
          low_quality: 'The photo is too blurry. Please try again and hold your device steady.',
        };
        setError(messages[code] ?? 'Invalid selfie. Please try again.');
      } else {
        setError('Failed to upload selfie. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  }, [capturedBlob, fetchMe, onCaptured]);

  // Capture + upload one angle (left, then right). Advances to the next angle,
  // and finishes (onCaptured) after the right angle or on skip.
  const handleCaptureAngle = useCallback(async () => {
    const frame = await captureSquareFrame();
    if (!frame) return;
    setAngleUploading(true);
    setError(null);
    try {
      await facePhotoApi.uploadAngle(frame.blob, angleStep);
      const done = new Set(anglesDone).add(angleStep);
      setAnglesDone(done);
      if (angleStep === 'left') {
        setAngleStep('right');
      } else {
        stopCamera();
        toast.success('All set — your matches will keep getting better!');
        onCaptured();
      }
    } catch (err: unknown) {
      if (facePhotoApi.isFaceValidationError(err)) {
        setError("We couldn't see your face clearly. Turn a little less and keep your face lit.");
      } else {
        setError('Could not save that angle. Try again or skip.');
      }
    } finally {
      setAngleUploading(false);
    }
  }, [captureSquareFrame, angleStep, anglesDone, stopCamera, onCaptured]);

  const handleFinishAngles = useCallback(() => {
    stopCamera();
    onCaptured();
  }, [stopCamera, onCaptured]);

  const handleClose = useCallback(() => {
    stopCamera();
    onClose();
  }, [stopCamera, onClose]);

  return (
    <>
    {legalModal && <LegalModal type={legalModal} isOpen onClose={() => setLegalModal(null)} />}
    <Modal isOpen={isOpen} onClose={required ? () => {} : handleClose} title="Find your photos" size="sm">
      <div className={styles.container}>
        {/* Hidden canvas for capturing frames */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {phase === 'intro' && (
          <>
            <div className={styles.iconWrap}>
              <Camera size={48} style={{ color: 'var(--clay-orange)' }} />
            </div>

            <p className={styles.heading}>Take a quick selfie</p>

            <p className={styles.description}>
              We'll use it to find every photo you appear in.
              {required
                ? ' A selfie is required to view your photos in this album.'
                : ' You can skip this and browse all photos instead.'}
            </p>

            <div className={styles.privacyBox}>
              <Shield size={14} style={{ color: 'var(--clay-orange)', flexShrink: 0 }} />
              <span>Your selfie is kept private and only used for photo matching. You can delete it anytime.</span>
            </div>

            <p className={styles.consentNote}>
              By proceeding, you acknowledge our{' '}
              <button className={styles.legalLink} onClick={() => setLegalModal('privacy')}>
                Privacy Policy
              </button>
              {' '}for biometric data use.
            </p>

            <div className={styles.actions}>
              <Button onClick={() => setPhase('camera')}>
                <Camera size={16} />
                Open Camera
              </Button>
              {!required && (
                <Button variant="ghost" onClick={handleClose}>
                  Maybe Later
                </Button>
              )}
            </div>
          </>
        )}

        {phase === 'camera' && (
          <>
            {cameraError ? (
              <div className={styles.cameraErrorWrap}>
                <VideoOff size={48} style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
                <p className={styles.cameraErrorText}>{cameraError}</p>
                <Button variant="ghost" onClick={() => setPhase('intro')}>
                  &larr; Go Back
                </Button>
              </div>
            ) : (
              <>
                <div className={styles.videoWrap}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={styles.video}
                    onCanPlay={() => setVideoReady(true)}
                  />
                  {/* Face guide overlay */}
                  <div className={styles.faceGuide} />
                  {!videoReady && <div className={styles.cameraLoadingRing} />}
                </div>

                <p className={styles.cameraHint}>
                  {videoReady ? 'Position your face inside the circle' : 'Starting camera…'}
                </p>

                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.captureBtn}
                    onClick={handleCapture}
                    disabled={!videoReady}
                    aria-label="Capture selfie"
                  >
                    <div className={styles.captureBtnInner} />
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {phase === 'preview' && previewUrl && (
          <>
            <div className={styles.previewWrap}>
              <img src={previewUrl} alt="Your selfie" className={styles.previewImg} />
            </div>

            {error && (
              <div className={styles.errorBox}>
                <X size={14} style={{ flexShrink: 0 }} />
                <span>{error}</span>
              </div>
            )}

            <p className={styles.previewHint}>
              Make sure your face is clearly visible and well-lit.
            </p>

            <div className={styles.actions}>
              <Button onClick={handleUpload} loading={uploading}>
                <Check size={16} />
                Use This Photo
              </Button>
              <Button variant="ghost" onClick={handleRetake} disabled={uploading}>
                <RefreshCw size={16} />
                Retake
              </Button>
            </div>
          </>
        )}

        {phase === 'angles' && (
          <>
            <div className={styles.iconWrap}>
              <Sparkles size={40} style={{ color: 'var(--clay-orange)' }} />
            </div>
            <p className={styles.heading}>
              Add a {angleStep} angle <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(optional)</span>
            </p>
            <p className={styles.description}>
              Turn your head slightly to the <strong>{angleStep}</strong> and capture. Two extra
              angles help us find you in photos where you're not facing the camera.
            </p>

            {cameraError ? (
              <div className={styles.cameraErrorWrap}>
                <VideoOff size={40} style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
                <p className={styles.cameraErrorText}>{cameraError}</p>
                <Button variant="ghost" onClick={handleFinishAngles}>Skip for now</Button>
              </div>
            ) : (
              <>
                <div className={styles.videoWrap}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={styles.video}
                    onCanPlay={() => setVideoReady(true)}
                  />
                  <div className={styles.faceGuide} />
                  {!videoReady && <div className={styles.cameraLoadingRing} />}
                </div>

                {error && (
                  <div className={styles.errorBox}>
                    <X size={14} style={{ flexShrink: 0 }} />
                    <span>{error}</span>
                  </div>
                )}

                <div className={styles.actions}>
                  <Button onClick={handleCaptureAngle} loading={angleUploading} disabled={!videoReady}>
                    <Camera size={16} />
                    Capture {angleStep} angle
                  </Button>
                  <Button variant="ghost" onClick={handleFinishAngles} disabled={angleUploading}>
                    {anglesDone.size > 0 ? 'Done' : 'Skip — the front selfie is enough'}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
    </>
  );
}
